import { Accordion, AccordionItemProps, AccordionItem as Item } from '@szhsin/react-accordion';
import { ReactElement, useEffect, useRef, useState } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import Switch from 'react-switch';
import { Tooltip } from 'react-tooltip';
import CTASqueezePopupModal from '../components/CTASqueezePopupModal';
import NavbarPlaceholder from '../components/NavbarPlaceholder';
import QuotationNavbar from '../components/QuotationNavbar';
import useDeviceSize, { LG_BREAKPOINT, MD_BREAKPOINT, SM_BREAKPOINT, XS_BREAKPOINT } from '../hooks/useDeviceSize';
import useEbcSolarStore, { COST_INCREASE_FACTOR, SolarPanelType, SolarPanelTypeData } from '../store/ebc-solar-store';
import accordionStyles from '../styles/accordion.module.css';
import monthlyBillSliderStyles from '../styles/monthlyBillSlider.module.css';
import packageItemStyles from '../styles/packageItem.module.css';

type StepperStepProps = {
    text: string;
    active: boolean;
};
const StepperStep = ({ text, active }: StepperStepProps) => {
    const { width, height, isMobile } = useDeviceSize();

    return (
        <div
            style={{
                width: width < XS_BREAKPOINT ? '100%' : '28.333%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: width < XS_BREAKPOINT ? 'center' : 'inherit',
                gap: '0.5rem',
            }}
        >
            {active ? (
                <img width={32} height='auto' alt='Tick' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenCircleTick.svg' />
            ) : (
                <img width={32} height='auto' alt='Tick' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenCircleTick.svg' />
            )}
            <span
                style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#212121',
                    lineHeight: 1.5,
                    textAlign: width < XS_BREAKPOINT ? 'center' : 'inherit',
                }}
            >
                {text}
            </span>
        </div>
    );
};

const StepperSpacer = () => {
    const { width, height, isMobile } = useDeviceSize();

    return (
        <div
            style={{
                width: width < XS_BREAKPOINT ? '2px' : '10%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <div
                style={{
                    width: width < XS_BREAKPOINT ? '2px' : '50%',
                    height: width < XS_BREAKPOINT ? '2rem' : '2px',
                    background: '#B9B9B9',
                }}
            />
        </div>
    );
};

const Stepper = () => {
    const { width, height, isMobile } = useDeviceSize();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: width < XS_BREAKPOINT ? 'column' : 'row',
                gap: width < XS_BREAKPOINT ? '0.5rem' : '0',
                alignItems: 'center',
                marginTop: '1rem',
            }}
        >
            <StepperStep text='Set up your system to see a price estimate' active={true} />
            {width >= XS_BREAKPOINT && <StepperSpacer />}
            <StepperStep text='Leave your contact details so we can ring you' active={true} />
            {width >= XS_BREAKPOINT && <StepperSpacer />}
            <StepperStep text='Book a time with our technical team agent to optimise your system' active={true} />
        </div>
    );
};

const AccordionItem = ({ header, ...rest }: AccordionItemProps) => (
    <Item
        {...rest}
        header={
            <>
                {header}
                <img className={accordionStyles.chevron} src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/chevronDown.svg' alt='Chevron Down' />
            </>
        }
        className={accordionStyles.item}
        buttonProps={{
            ...rest.buttonProps,
            className: ({ isEnter }) => `${accordionStyles.itemBtn} ${isEnter && accordionStyles.itemBtnExpanded}`,
        }}
        contentProps={{ className: accordionStyles.itemContent }}
        panelProps={{ className: accordionStyles.itemPanel }}
    />
);

type MoneySavedBoxProps = {
    id: string;
    iconSrc: string;
    title: string;
    subtitle: string;
    description: string;
};
const MoneySavedBox = ({ id, iconSrc, title, subtitle, description }: MoneySavedBoxProps) => {
    const { width, height, isMobile } = useDeviceSize();

    return (
        <div
            style={{
                width: width < XS_BREAKPOINT ? '100%' : '50%',
                display: 'flex',
                padding: '0.5rem',
            }}
        >
            <div
                style={{
                    width: '100%',
                    padding: width < MD_BREAKPOINT ? '1rem' : '2rem',
                    display: 'flex',
                    position: 'relative',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                    background: '#98E8B8',
                    borderRadius: '8px',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        cursor: 'pointer',
                    }}
                >
                    <a data-tooltip-id={`tooltip-${id}`}>
                        <img width={16} height='auto' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/InfoIcon.svg' alt='Info Icon' />
                    </a>
                    <Tooltip
                        id={`tooltip-${id}`}
                        content={description}
                        opacity={1}
                        clickable
                        noArrow
                        style={{
                            width: isMobile ? '200px' : '264px',
                            display: 'flex',
                            borderRadius: '8px',
                            color: '#212121',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                            zIndex: 9999,
                            lineHeight: 1.5,
                        }}
                        openOnClick
                    />
                </div>

                <img width={50} height='auto' src={iconSrc} alt='Money Pig' />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    <span style={{ fontSize: '20px', fontWeight: 700, color: '#212121', lineHeight: '1.5', textAlign: 'center' }}>{title}</span>
                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#212121', lineHeight: '1.5', textAlign: 'center' }}>{subtitle}</span>
                </div>
            </div>
        </div>
    );
};

const MoneySavedCalculator = () => {
    const { width, height, isMobile } = useDeviceSize();

    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setFocus] = useState<boolean>(false);

    const { monthlyBill, setMonthlyBill, quotationData, setQuotationData, calculateQuotationData } = useEbcSolarStore((state) => ({
        monthlyBill: state.monthlyBill,
        setMonthlyBill: state.setMonthlyBill,
        quotationData: state.quotationData,
        setQuotationData: state.setQuotationData,
        calculateQuotationData: state.calculateQuotationData,
    }));

    const handleSetMonthlyBill = (x: number) => {
        setMonthlyBill(x);
        calculateQuotationData();
    };

    const solarPanelLifeSpan = SolarPanelTypeData[quotationData.solarPanelType].lifespanYears;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.onfocus = () => {
                setFocus(true);
            };
            inputRef.current.onblur = () => {
                setFocus(false);
            };
        }

        return () => {
            if (inputRef.current) {
                inputRef.current.onfocus = null;
                inputRef.current.onblur = null;
            }
        };
    }, [inputRef.current]);

    return (
        <Accordion
            transition
            transitionTimeout={250}
            style={{
                background: '#C3F0D5',
                borderRadius: '0px 0px 8px 8px',
            }}
        >
            <AccordionItem
                header='How much money will I save?'
                buttonProps={{ style: { padding: width < XS_BREAKPOINT ? '2rem' : '2rem 4rem 2rem 4rem' } }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: width < SM_BREAKPOINT ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: width < XS_BREAKPOINT ? '2rem' : '2rem 4rem 2rem 4rem',
                    }}
                >
                    <div
                        style={{
                            width: width < SM_BREAKPOINT ? '100%' : '50%',
                            display: 'flex',
                            padding: width < SM_BREAKPOINT ? '0.5rem' : 0,
                        }}
                    >
                        <div
                            style={{
                                width: width < SM_BREAKPOINT ? '100%' : '80%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '0.5rem',
                                }}
                            >
                                <span style={{ fontSize: width < XS_BREAKPOINT ? '20px' : '24px', fontWeight: 700, color: '#212121' }}>
                                    What's your monthly bill?
                                </span>
                                <a data-tooltip-id='tooltip-monthly-bill-explainer'>
                                    <img style={{ cursor: 'pointer' }} width={20} height='auto' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/InfoIcon.svg' alt='Info Icon' />
                                </a>
                                <Tooltip
                                    id='tooltip-monthly-bill-explainer'
                                    content='Your monthly bill is used to calculate more accurate savings over the lifetime of your system.'
                                    opacity={1}
                                    clickable
                                    noArrow
                                    style={{
                                        width: isMobile ? '200px' : '264px',
                                        display: 'flex',
                                        borderRadius: '8px',
                                        color: '#212121',
                                        backgroundColor: '#FFFFFF',
                                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                                        zIndex: 9999,
                                        lineHeight: 1.5,
                                    }}
                                    openOnClick
                                />
                            </div>
                            <span style={{ fontSize: width < XS_BREAKPOINT ? '14px' : '16px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                Your monthly bill is used to calculate more accurate savings
                            </span>

                            <input
                                name='monthlyBill'
                                ref={inputRef}
                                value={monthlyBill}
                                onChange={(e) => {
                                    handleSetMonthlyBill(parseInt(e.target.value));
                                }}
                                style={{
                                    width: '50%',
                                    padding: '1rem',
                                    borderRadius: '4px',
                                    border: 'none',
                                    background: '#FFF',
                                    fontSize: '18px',
                                    fontWeight: '500',
                                    fontFamily: 'Montserrat',
                                    color: '#212121',
                                    transition: 'border 0.2s ease-in-out',
                                }}
                                type='number'
                            />

                            <input
                                value={monthlyBill}
                                onChange={(e) => {
                                    handleSetMonthlyBill(parseInt(e.target.value));
                                }}
                                type='range'
                                min={10}
                                max={500}
                                step={10}
                                className={monthlyBillSliderStyles.slider}
                            />
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span>£10</span>
                                <span>£500</span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            width: width < SM_BREAKPOINT ? '100%' : '50%',
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                        }}
                    >
                        <MoneySavedBox
                            id='total-savings'
                            iconSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenMoneyPig.svg'
                            title={`£${quotationData.totalEstimatedSavingsRangeStart.toLocaleString()}-£${quotationData.totalEstimatedSavingsRangeEnd.toLocaleString()}`}
                            subtitle='Total savings'
                            description={`Estimated over ${solarPanelLifeSpan} years with a yearly electricity price increase of ${(
                                (COST_INCREASE_FACTOR - 1) *
                                100
                            ).toFixed(1)}%.`}
                        />
                        <MoneySavedBox
                            id='monthly-savings'
                            iconSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenDecline.svg'
                            title={`£${Math.round(quotationData.estimatedMonthlySavingsRangeStart).toLocaleString()}-£${Math.round(
                                quotationData.estimatedMonthlySavingsRangeEnd
                            ).toLocaleString()}`}
                            subtitle='Monthly savings'
                            description='The monthly savings are calculated as the average savings for the first year. This includes electricity sold back to the grid.'
                        />
                        <MoneySavedBox
                            id='yearly-co2-saved'
                            iconSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenGlobe.svg'
                            title={`${Math.round(quotationData.estimatedYearlyCO2Savings).toLocaleString()} kg`}
                            subtitle='CO2 saved/year'
                            description="The yearly CO2-savings are calculated based on numbers from Google's Solar data."
                        />
                        <MoneySavedBox
                            id='self-consumption'
                            iconSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenPlug.svg'
                            title={`${quotationData.selfConsumptionPctRangeStart}-${quotationData.selfConsumptionPctRangeEnd} %`}
                            subtitle='Self consumption'
                            description='The self consumption rate is how much of the produced energy you use yourself. Selling energy to the grid will usually be more expensive than buying, therefore will a high rate increase your savings.'
                        />
                    </div>
                </div>
            </AccordionItem>
        </Accordion>
    );
};

const SavingsSection = () => {
    const { width, height, isMobile } = useDeviceSize();

    const { monthlyBill, totalEstimatedSavingsRangeStart, totalEstimatedSavingsRangeEnd, estimatedAnnualKwhProduction, solarPanelType } =
        useEbcSolarStore((state) => ({
            monthlyBill: state.monthlyBill,
            totalEstimatedSavingsRangeStart: state.quotationData.totalEstimatedSavingsRangeStart,
            totalEstimatedSavingsRangeEnd: state.quotationData.totalEstimatedSavingsRangeEnd,
            estimatedAnnualKwhProduction: state.quotationData.estimatedAnnualKwhProduction,
            solarPanelType: state.quotationData.solarPanelType,
        }));

    return (
        <>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: width < SM_BREAKPOINT ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '4rem',
                    padding: width < XS_BREAKPOINT ? '2rem' : '4rem',
                    borderRadius: '8px 8px 0px 0px',
                    background: 'linear-gradient(190deg, rgba(255, 255, 255, 0.2) 30%, rgba(198, 198, 198, 0.2) 70%)',
                    gap: width < MD_BREAKPOINT ? '2rem' : '0',
                }}
            >
                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : width < MD_BREAKPOINT ? '50%' : '70%',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1rem',
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                                borderRadius: '4px',
                                background: '#A9D3BA',
                            }}
                        >
                            <img width={45} src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenMoneyPig.svg' alt='Savings Pig' />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                        }}
                    >
                        <span style={{ fontSize: '16px', color: '#212121' }}>ESTIMATED SAVINGS</span>
                        <span style={{ fontSize: '30px', fontWeight: 700, color: '#212121' }}>
                            £{totalEstimatedSavingsRangeStart.toLocaleString()}-£{totalEstimatedSavingsRangeEnd.toLocaleString()}
                        </span>
                    </div>
                </div>

                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : width < MD_BREAKPOINT ? '50%' : '30%',
                    }}
                >
                    <span style={{ fontSize: '16px', color: '#212121', lineHeight: 1.5 }}>
                        The estimated savings over {SolarPanelTypeData[solarPanelType].lifespanYears} years are based on a monthly bill of{' '}
                        <span style={{ fontWeight: 700, color: '#26A757' }}>£{monthlyBill.toLocaleString()}</span> and the selected package production
                        of <span style={{ fontWeight: 700, color: '#26A757' }}>{Math.round(estimatedAnnualKwhProduction).toLocaleString()} kWh</span>.
                    </span>
                </div>
            </div>

            <MoneySavedCalculator />
        </>
    );
};

type PackageItemDrawerProps = {
    open: boolean;
    setOpen: (x: boolean) => void;
    content?: ReactElement;
};
const PackageItemDrawer = ({ open, setOpen, content }: PackageItemDrawerProps) => {
    const { width, height, isMobile } = useDeviceSize();

    const { quotationData, setCtaSqueezePopupModalOpen } = useEbcSolarStore((state) => ({
        quotationData: state.quotationData,
        setCtaSqueezePopupModalOpen: state.setCtaSqueezePopupModalOpen,
    }));

    const handleGetAnOffer = () => {
        setCtaSqueezePopupModalOpen(true);
    };

    // console.log('rendering drawer');
    // console.log(open);

    return (
        <Drawer
            open={open}
            direction='right'
            onClose={() => setOpen(false)}
            zIndex={9999}
            lockBackgroundScroll={true}
            overlayClassName={packageItemStyles.packageItemDrawerOverlay}
            style={{
                maxWidth: '90vw',
                width: '500px',
                overflowY: 'scroll',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    background: '#212121',
                    padding: '1rem',
                    gap: '1rem',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1rem',
                        alignItems: 'center',
                    }}
                >
                    <div
                        onClick={() => setOpen(false)}
                        style={{
                            cursor: 'pointer',
                        }}
                    >
                        <img width={18} height='auto' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/WhiteClose.svg' alt='Close' />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                        }}
                    >
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>Price</span>
                        <span style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF' }}>£{quotationData.totalPrice.toLocaleString()}</span>
                    </div>
                </div>
                <div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingLeft: width < XS_BREAKPOINT ? '1rem' : '4rem',
                            paddingRight: width < XS_BREAKPOINT ? '1rem' : '4rem',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            borderRadius: '32px',
                            color: '#FFFFFF',
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: 700,
                            background: '#26A757',
                            cursor: 'pointer',
                            textWrap: 'nowrap',
                        }}
                        onClick={handleGetAnOffer}
                    >
                        Confirm Your Price
                    </div>
                </div>
            </div>

            <div style={{ height: '100%' }}>{content}</div>
        </Drawer>
    );
};

type PackageItemProps = {
    imgSrc: string;
    title: string;
    subtitle: string;
    btn?: ReactElement;
    drawerContent?: ReactElement;
    noDrawer?: boolean;
};
const PackageItem = ({ imgSrc, title, subtitle, btn, drawerContent, noDrawer }: PackageItemProps) => {
    const { width, height, isMobile } = useDeviceSize();

    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    const handleOpenDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    return (
        <>
            <PackageItemDrawer open={drawerOpen} setOpen={setDrawerOpen} content={drawerContent} />
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: width < XS_BREAKPOINT ? 'column' : 'row',
                    alignItems: width < XS_BREAKPOINT ? 'center' : 'normal',
                    borderRadius: '8px',
                    background: '#FFFFFF',
                    cursor: noDrawer === true ? 'default' : 'pointer',
                }}
                className={noDrawer === true ? '' : packageItemStyles.packageItem}
                onClick={(e) => {
                    if (!e.defaultPrevented && !noDrawer) {
                        handleOpenDrawer();
                    }
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: width < XS_BREAKPOINT ? '100%' : '25%',
                        background: '#26A757',
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                    }}
                >
                    <img width={width < XS_BREAKPOINT ? '128px' : '100%'} height='auto' src={imgSrc} alt='Package Icon' />
                </div>
                <div
                    style={{
                        width: width < XS_BREAKPOINT ? '100%' : width < MD_BREAKPOINT ? '55%' : '60%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: width < XS_BREAKPOINT ? 'center' : 'flex-start',
                        justifyContent: 'center',
                        paddingTop: width < XS_BREAKPOINT ? '1rem' : '0.25rem',
                        paddingBottom: width < XS_BREAKPOINT ? '1rem' : '0.25rem',
                        paddingLeft: width < XS_BREAKPOINT ? '0.5rem' : width < MD_BREAKPOINT ? '1rem' : '2rem',
                        paddingRight: width < XS_BREAKPOINT ? '0.5rem' : '0',
                        gap: width < MD_BREAKPOINT ? '0.5rem' : '1rem',
                    }}
                    className={packageItemStyles.packageItemContent}
                >
                    <span
                        style={{
                            fontSize: width < XS_BREAKPOINT ? '18px' : width < SM_BREAKPOINT ? '22px' : width < MD_BREAKPOINT ? '18px' : '22px',
                            fontWeight: 700,
                            color: '#212121',
                            textAlign: width < XS_BREAKPOINT ? 'center' : 'left',
                        }}
                    >
                        {title}
                    </span>
                    <span
                        style={{
                            fontSize: width < XS_BREAKPOINT ? '14px' : width < SM_BREAKPOINT ? '16px' : width < MD_BREAKPOINT ? '14px' : '16px',
                            fontWeight: 600,
                            color: '#26A757',
                            textAlign: width < XS_BREAKPOINT ? 'center' : 'left',
                        }}
                    >
                        {subtitle}
                    </span>
                </div>

                <div
                    style={{
                        width: width < XS_BREAKPOINT ? '100%' : width < MD_BREAKPOINT ? '20%' : '15%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    className={packageItemStyles.packageItemContent}
                >
                    <div
                        style={{
                            zIndex: 998,
                        }}
                    >
                        {btn || null}
                    </div>
                </div>
            </div>
        </>
    );
};

const PackageSection = () => {
    const { width, height, isMobile } = useDeviceSize();

    const { quotationData, setQuotationData, calculateQuotationData } = useEbcSolarStore((state) => ({
        quotationData: state.quotationData,
        setQuotationData: state.setQuotationData,
        calculateQuotationData: state.calculateQuotationData,
    }));

    const toggleBatteryEnabled = () => {
        setQuotationData({
            ...useEbcSolarStore.getState().quotationData,
            batteryEnabled: !quotationData.batteryEnabled,
        });
        calculateQuotationData();
    };

    return (
        <div
            id='package-container'
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: width < SM_BREAKPOINT ? '2rem' : '2rem 4rem 2rem 4rem',
                background: 'linear-gradient(186.56deg, rgba(198, 198, 198, 0.2) 4.66%, rgba(198, 198, 198, 0.2) 94.85%)',
                borderRadius: '8px',
                gap: '2rem',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: width < SM_BREAKPOINT ? 'column' : 'row',
                    alignItems: 'center',
                    paddingTop: '1rem',
                    paddingBottom: '1rem',
                    gap: width < SM_BREAKPOINT ? '1rem' : '4rem',
                }}
            >
                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '1rem',
                    }}
                >
                    <span style={{ fontSize: width < SM_BREAKPOINT ? '28px' : '32px', fontWeight: 700, color: '#212121' }}>This is your system</span>
                </div>

                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                    }}
                >
                    <span style={{ fontSize: '16px', fontWeight: 500, lineHeight: 1.5, color: '#212121' }}>
                        This recommended system takes into account savings potential for your location as well as optimal electricity production
                        conditions.
                    </span>
                </div>
            </div>

            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: width < SM_BREAKPOINT ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: width < SM_BREAKPOINT ? 'center' : 'inherit',
                    gap: width < XS_BREAKPOINT ? '4rem' : width < SM_BREAKPOINT ? '2rem' : '4rem',
                }}
            >
                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: width < SM_BREAKPOINT ? '0.5rem' : '1rem',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                        <img width={32} src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/BlackSolarPanel.svg' />
                        <span style={{ fontSize: '22px', fontWeight: 700, color: '#212121' }}>PV System</span>
                    </div>

                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-1.png'
                        title='SOLAR PANELS'
                        subtitle={`${quotationData.numberOfSolarPanels} × ${SolarPanelTypeData[quotationData.solarPanelType].name} panels`}
                        btn={<img width={width < SM_BREAKPOINT ? '46px' : width < MD_BREAKPOINT ? '32px' : '46px'} src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GrayEditPencil.svg' />}
                        drawerContent={
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '2rem',
                                        gap: '2rem',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                        }}
                                    >
                                        <span style={{ fontSize: width < XS_BREAKPOINT ? '18px' : '22px', fontWeight: 600, color: '#212121' }}>
                                            Modify Panel Type
                                        </span>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: width < XS_BREAKPOINT ? 'column' : 'row',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            {[SolarPanelType.PREMIUM, SolarPanelType.PREMIUM_PLUS].map((solarPanelType) => {
                                                const solarPanelTypeData = SolarPanelTypeData[solarPanelType];
                                                return (
                                                    <div
                                                        key={solarPanelType}
                                                        style={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: '1rem',
                                                            borderRadius: '8px',
                                                            border: '2px solid #B9B9B9',
                                                            borderColor: solarPanelType === quotationData.solarPanelType ? '#26A757' : '#B9B9B9',
                                                            cursor: 'pointer',
                                                            gap: '0.5rem',
                                                        }}
                                                        onClick={() => {
                                                            if (solarPanelType === quotationData.solarPanelType) {
                                                                return;
                                                            }
                                                            setQuotationData({
                                                                ...quotationData,
                                                                solarPanelType: solarPanelType,
                                                            });
                                                            calculateQuotationData();
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#212121', textAlign: 'center' }}>
                                                            {solarPanelTypeData.name}
                                                        </span>
                                                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#212121', textAlign: 'center' }}>
                                                            {solarPanelTypeData.wattage} W
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                        }}
                                    >
                                        <span style={{ fontSize: width < XS_BREAKPOINT ? '18px' : '22px', fontWeight: 600, color: '#212121' }}>
                                            Modify Panel Amount
                                        </span>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: width < XS_BREAKPOINT ? 'column' : 'row',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            {[5, 10, 15].map((numberOfSolarPanels) => {
                                                return (
                                                    <div
                                                        key={numberOfSolarPanels}
                                                        style={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: '1rem',
                                                            borderRadius: '8px',
                                                            border: '2px solid #B9B9B9',
                                                            borderColor:
                                                                numberOfSolarPanels === quotationData.numberOfSolarPanels ? '#26A757' : '#B9B9B9',
                                                            cursor: 'pointer',
                                                            gap: '0.5rem',
                                                        }}
                                                        onClick={() => {
                                                            if (numberOfSolarPanels === quotationData.numberOfSolarPanels) {
                                                                return;
                                                            }
                                                            setQuotationData({
                                                                ...quotationData,
                                                                numberOfSolarPanels: numberOfSolarPanels,
                                                            });
                                                            calculateQuotationData();
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#212121', textAlign: 'center' }}>
                                                            {numberOfSolarPanels}
                                                        </span>
                                                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#212121', textAlign: 'center' }}>
                                                            {(
                                                                (numberOfSolarPanels * SolarPanelTypeData[quotationData.solarPanelType].wattage) /
                                                                1000
                                                            ).toFixed(1)}{' '}
                                                            kWp
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '2rem',
                                            gap: '0.5rem',
                                            border: '1px solid #B9B9B9',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            <Switch onColor='#26A757' checked={quotationData.batteryEnabled} onChange={toggleBatteryEnabled} />
                                            <span style={{ fontSize: '20px', fontWeight: 600, color: '#212121' }}>Battery</span>
                                        </div>
                                        <span>Add a battery to increase self-consumption.</span>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                        background: '#C3F0D5',
                                        padding: '2rem',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 600, color: '#212121' }}>
                                            {quotationData.numberOfSolarPanels} × {SolarPanelTypeData[quotationData.solarPanelType].name} panels (
                                            {SolarPanelTypeData[quotationData.solarPanelType].wattage} W)
                                        </span>
                                    </div>
                                    <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        {SolarPanelTypeData[quotationData.solarPanelType].description}
                                    </span>
                                    <div>
                                        <img width='100%' src={SolarPanelTypeData[quotationData.solarPanelType].imgSrc} alt='Solar Panel' />
                                    </div>
                                </div>
                            </div>
                        }
                    />
                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-2.png'
                        title='BATTERY'
                        subtitle='Learn More'
                        btn={
                            <Switch
                                width={width < SM_BREAKPOINT ? 56 : width < MD_BREAKPOINT ? 42 : 56}
                                height={width < SM_BREAKPOINT ? 28 : width < MD_BREAKPOINT ? 21 : 28}
                                handleDiameter={width < SM_BREAKPOINT ? 26 : width < MD_BREAKPOINT ? 19 : 26}
                                onColor='#26A757'
                                checked={quotationData.batteryEnabled}
                                onChange={(checked, event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    toggleBatteryEnabled();
                                }}
                            />
                        }
                        drawerContent={
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '2rem',
                                        gap: '2rem',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '2rem',
                                            gap: '0.5rem',
                                            border: '1px solid #B9B9B9',
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            <Switch onColor='#26A757' checked={quotationData.batteryEnabled} onChange={toggleBatteryEnabled} />
                                            <span style={{ fontSize: '20px', fontWeight: 600, color: '#212121' }}>Battery</span>
                                        </div>
                                        <span>Add a battery to increase self-consumption.</span>
                                    </div>

                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        Batteries can help lower bills. Batteries store the solar energy you produce but aren't able to use at that
                                        moment so you can offset buying grid electricity when the sun doesn't shine.
                                    </span>
                                </div>

                                <div
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                        background: '#C3F0D5',
                                        padding: '2rem',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 600, color: '#212121' }}>
                                            PureStorage II Battery
                                        </span>
                                    </div>
                                    <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        The PureStorage II 5kWh is a safe, high-performance lithium-iron-phosphate battery, compatible with top
                                        inverters like Solis, and ideal for expanding solar panel systems up to 25kWh. It's durable, waterproof, easy
                                        to install, and works well both indoors and outdoors.
                                    </span>
                                    <div>
                                        <img width='100%' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Puredrive-II-Battery.webp' alt='Puredrive II Battery' />
                                    </div>
                                </div>
                            </div>
                        }
                    />
                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-3.png'
                        title={`${(100 * Math.round(quotationData.estimatedAnnualKwhProduction / 100)).toLocaleString()} kWH`}
                        subtitle='Annual Production'
                        noDrawer
                    />
                </div>

                <div
                    style={{
                        width: width < SM_BREAKPOINT ? '100%' : '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: width < SM_BREAKPOINT ? '0.5rem' : '1rem',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                        <img width={32} src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/BlackPresent.svg' />
                        <span style={{ fontSize: '22px', fontWeight: 700, color: '#212121' }}>Included services</span>
                    </div>

                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-4.png'
                        title='INSTALLATION SERVICE'
                        subtitle='Learn More'
                        drawerContent={
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '2rem',
                                        gap: '2rem',
                                    }}
                                >
                                    <span style={{ fontSize: '24px', fontWeight: 700, color: '#212121', lineHeight: 1.5 }}>Installation service</span>

                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        A team of certified technicians will come to your home at a time agreed on with you. It typically takes one or
                                        two days to attach the panels to your roof. Then the electrician connects the system and puts it into
                                        operation.
                                    </span>
                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        Installation services are included in your price.
                                    </span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        padding: '2rem',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <img width='100%' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/solar-img-2.jpg' />
                                </div>
                            </div>
                        }
                    />
                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-5.png'
                        title='EBC GUARANTEE'
                        subtitle='Learn More'
                        drawerContent={
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '2rem',
                                        gap: '2rem',
                                    }}
                                >
                                    <span style={{ fontSize: '24px', fontWeight: 700, color: '#212121', lineHeight: 1.5 }}>Our Guarantee</span>

                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        We make your solar system installation worry-free. The UK industry standard is a two year workmanship
                                        warranty.
                                    </span>
                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        Installation services are included in your price.
                                    </span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        padding: '2rem',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <img width='100%' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/solar-img-6.jpg' />
                                </div>
                            </div>
                        }
                    />
                    <PackageItem
                        imgSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/Package-Item-6.png'
                        title='MONITORING APP'
                        subtitle='Learn More'
                        drawerContent={
                            <div
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: '2rem',
                                        gap: '2rem',
                                    }}
                                >
                                    <span style={{ fontSize: '24px', fontWeight: 700, color: '#212121', lineHeight: 1.5 }}>
                                        Monitor your production with solar app
                                    </span>

                                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#212121', lineHeight: 1.5 }}>
                                        How much solar energy do you produce? Monitor and understand your solar system with the solar app. Track your
                                        production and gain insights into how your system is performing. Also keep an eye on your climate impact.
                                    </span>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        padding: '2rem',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <img width='100%' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/solar-img-3.jpg' />
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

type CTADetailsItemProps = {
    imageSrc: string;
    title: string;
    description: string;
};
const CTADetailsItem = ({ imageSrc, title, description }: CTADetailsItemProps) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
            }}
        >
            <div
                style={{
                    maxWidth: 'none',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    background: '#A9D3BA',
                    borderRadius: '4px',
                }}
            >
                <img width={24} src={imageSrc} style={{maxWidth: 'none'}} />
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                }}
            >
                <span style={{ fontSize: '18px', fontWeight: 600, lineHeight: '1.5' }}>{title}</span>
                <span style={{ fontSize: '16px', fontWeight: 500, lineHeight: '1.5' }}>{description}</span>
            </div>
        </div>
    );
};

const CTASection = () => {
    const { width, height, isMobile } = useDeviceSize();

    const { price, numSolarPanels, solarPanelType, productionCapacity, batteryEnabled, setCtaSqueezePopupModalOpen } = useEbcSolarStore((state) => ({
        price: state.quotationData.totalPrice,
        numSolarPanels: state.quotationData.numberOfSolarPanels,
        solarPanelType: state.quotationData.solarPanelType,
        productionCapacity: state.quotationData.estimatedKilowattPeak,
        batteryEnabled: state.quotationData.batteryEnabled,
        setCtaSqueezePopupModalOpen: state.setCtaSqueezePopupModalOpen,
    }));

    const handleGetAnOffer = () => {
        setCtaSqueezePopupModalOpen(true);
    };

    const handleEditPackage = () => {
        // Scroll to #package-container
        const packageContainer = document.getElementById('package-container');
        if (packageContainer) {
            packageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '1rem' : '0',
            }}
        >
            <div
                style={{
                    width: isMobile ? '100%' : '50%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        width: isMobile ? '100%' : '66%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <span
                        style={{
                            fontSize: width < SM_BREAKPOINT ? '28px' : width < MD_BREAKPOINT ? '32px' : '40px',
                            fontWeight: 700,
                            color: '#212121',
                        }}
                    >
                        Price estimate
                    </span>

                    {/* <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '1rem',
                            gap: '0.5rem',
                        }}
                    >
                        <span style={{ fontSize: width < SM_BREAKPOINT ? '16px' : '18px', fontWeight: 700, color: '#212121', lineHeight: '1.5' }}>
                            Buy Now
                        </span>
                        <span style={{ fontSize: width < SM_BREAKPOINT ? '16px' : '18px', fontWeight: 500, color: '#212121', lineHeight: '1.5' }}>
                            With Buy Now you pay no deposit. You are invoiced once the installation is on your roof.
                        </span>
                    </div> */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '2rem',
                            gap: '0.5rem',
                        }}
                    >
                        <span style={{ fontSize: width < SM_BREAKPOINT ? '16px' : '18px', fontWeight: 700, color: '#212121', lineHeight: '1.5' }}>
                            Sell the electricity you don't use
                        </span>
                        <span style={{ fontSize: width < SM_BREAKPOINT ? '16px' : '18px', fontWeight: 500, color: '#212121', lineHeight: '1.5' }}>
                            When you produce more electricity than you use, your electricity provider will buy your excess electricity from you.
                        </span>
                    </div>
                </div>
            </div>

            <div
                style={{
                    width: isMobile ? '100%' : '50%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                {/* <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                        borderRadius: '32px',
                        background: '#C3F0D5',
                        border: '1px solid #212121',
                        cursor: 'pointer',
                    }}
                >
                    <span style={{ fontSize: '18px', fontWeight: 700, color: '#212121' }}>Direct</span>
                </div> */}

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1rem',
                        paddingBottom: '3rem',
                        borderRadius: '8px',
                        background: 'rgba(198, 198, 198, 0.2)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span style={{ width: '50%', fontSize: '22px', fontWeight: 500, color: '#212121' }}>Solar Package</span>
                        <span style={{ width: '50%', fontSize: '22px', fontWeight: 500, color: '#212121', textAlign: 'right' }}>
                            £{price.toLocaleString()}
                        </span>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            marginTop: '1rem',
                        }}
                    >
                        <span style={{ fontSize: '16px', fontWeight: 500, color: '#212121' }}>
                            {numSolarPanels} × {SolarPanelTypeData[solarPanelType].name} panels - {productionCapacity.toFixed(1)} kWp
                        </span>
                        <span style={{ fontSize: '16px', fontWeight: 500, color: '#212121' }}>1 × Inverter</span>
                        <span style={{ fontSize: '16px', fontWeight: 500, color: '#212121' }}>1 × PV Smart Meter</span>
                        {batteryEnabled === true && <span style={{ fontSize: '16px', fontWeight: 500, color: '#212121' }}>1 × Solar Storage Battery</span>}
                    </div>

                    <div
                        style={{
                            width: '100%',
                            height: '1px',
                            marginTop: '2rem',
                            marginBottom: '2rem',
                            background: '#B9B9B9',
                        }}
                    />

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span
                            style={{
                                width: width < SM_BREAKPOINT ? '70%' : '50%',
                                fontSize: width < SM_BREAKPOINT ? '22px' : '26px',
                                fontWeight: 600,
                                color: '#212121',
                            }}
                        >
                            Final cost (incl. VAT, if applicable)
                        </span>
                        <span
                            style={{
                                width: width < SM_BREAKPOINT ? '30%' : '50%',
                                fontSize: width < SM_BREAKPOINT ? '18px' : '22px',
                                fontWeight: 600,
                                color: '#212121',
                                textAlign: 'right',
                            }}
                        >
                            £{price.toLocaleString()}
                        </span>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '0.25rem',
                            marginTop: '1rem',
                            cursor: 'pointer',
                        }}
                    >
                        <Accordion>
                            <Item
                                header={
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: '0.25rem',
                                        }}
                                    >
                                        <img width={24} height='auto' src='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GrayPlus.svg' alt='Expand' />
                                        <span style={{ fontSize: '12px', fontWeight: 500, color: '#212121' }}>Details</span>
                                    </div>
                                }
                                buttonProps={{
                                    className: ({ isEnter }) => `${accordionStyles.detailsItemsBtn} ${isEnter && accordionStyles.detailsItemsBtnExpanded}`,
                                }}
                                style={{
                                    cursor: 'default',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4rem',
                                    }}
                                >
                                    <CTADetailsItem
                                        imageSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenIncrease.svg'
                                        title='Return on investment'
                                        description='You can reduce your electricity bill from day 1 and the average payback period is around 8 years in the UK. Panels last 20-30 years.'
                                    />
                                    <CTADetailsItem
                                        imageSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenSolarPlug.svg'
                                        title="You choose what's right for you"
                                        description='Do you need batteries to optimise your solar production utilisation? Would you like to sell your excess electricity to the grid? We advise you on every step of the process with clear, straightforward advice.'
                                    />
                                    <CTADetailsItem
                                        imageSrc='https://storage.googleapis.com/ebc-solar-quotation-widget/assets/GreenShield.svg'
                                        title='High-quality service'
                                        description="We're confident in our service so you pay just 60% upfront - and the rest you only pay after your system is installed."
                                    />
                                </div>
                            </Item>
                        </Accordion>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingLeft: isMobile ? '1rem' : '4rem',
                            paddingRight: isMobile ? '1rem' : '4rem',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            borderRadius: '32px',
                            color: '#FFFFFF',
                            fontSize: '16px',
                            fontWeight: 700,
                            background: '#26A757',
                            cursor: 'pointer',
                        }}
                        onClick={handleGetAnOffer}
                    >
                        Confirm Your Price
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingLeft: isMobile ? '1rem' : '4rem',
                            paddingRight: isMobile ? '1rem' : '4rem',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            borderRadius: '32px',
                            color: '#26A757',
                            fontSize: '16px',
                            fontWeight: 700,
                            background: 'transparent',
                            cursor: 'pointer',
                        }}
                        onClick={handleEditPackage}
                    >
                        Edit Package
                    </div>
                </div>

                <span style={{ fontSize: '18px', fontWeight: 700, color: '#212121', marginTop: '1rem' }}>
                    This price is an estimate, contact us for a precise offer.
                </span>
            </div>
        </div>
    );
};

export const QuotationOverview = () => {
    const { width, height, isMobile } = useDeviceSize();

    const { address, price, ctaSqueezePopupModalOpen, setCtaSqueezePopupModalOpen } = useEbcSolarStore((state) => ({
        address: state.address,
        price: state.quotationData.totalPrice,
        ctaSqueezePopupModalOpen: state.ctaSqueezePopupModalOpen,
        setCtaSqueezePopupModalOpen: state.setCtaSqueezePopupModalOpen,
    }));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <NavbarPlaceholder />
            <QuotationNavbar price={price} />
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: width < LG_BREAKPOINT ? '80%' : '1280px',
                        display: 'flex',
                        flexDirection: 'column',
                        marginTop: isMobile ? '5rem' : '2rem',
                        paddingBottom: '2rem',
                    }}
                >
                    <div>
                        <h1 style={{ fontSize: width < XS_BREAKPOINT ? '36px' : '46px', fontWeight: 600, color: '#212121' }}>
                            Here's your solar estimate
                        </h1>
                        <p style={{ fontSize: '18px', fontWeight: 400, color: '#212121', marginTop: '1rem' }}>This is an estimate for {address}</p>
                    </div>

                    <div style={{ marginTop: '2rem' }}>
                        <h2 style={{ fontSize: width < XS_BREAKPOINT ? '20px' : '24px', fontWeight: 600, color: '#212121' }}>How does this work?</h2>
                        <Stepper />
                    </div>

                    <div style={{ marginTop: '4rem' }}>
                        <SavingsSection />
                    </div>

                    <div style={{ marginTop: '4rem' }}>
                        <PackageSection />
                    </div>

                    <div style={{ marginTop: '4rem' }}>
                        <CTASection />
                    </div>

                    <CTASqueezePopupModal open={ctaSqueezePopupModalOpen} onClose={() => setCtaSqueezePopupModalOpen(false)} />
                </div>
            </div>
        </div>
    );
};

export default QuotationOverview;
